/* eslint-disable react-hooks/exhaustive-deps */
import { ButtonPrimary } from '@/components/button'
import { Close } from '@/components/icons'
import { LIMIT, OFFSET } from '@/constants/Common'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { useSearchPlayer } from '@/services'
import { Player } from '@/types'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from '../../../components/search'
import { PlayerItemLoading } from '../../../components/skeleton'
import { BracketPlayerItem } from './BracketPlayerItem'
import { ViewQualifyingBracketsModal } from './ViewQualifyingBracketsModal'
type Props = {
  open: boolean
  onClose: () => void
  onSubmit: (value?: any) => any
  currentBracketData: any
  currentPlayer?: any
  viewQualifyingBrackets?: boolean
  closeViewBrackets?: () => void
}

export const EventRegisterAddPartnerModal = ({
  open,
  onClose,
  onSubmit,
  currentBracketData,
  currentPlayer,
  viewQualifyingBrackets,
  closeViewBrackets,
}: Props) => {
  const { t } = useTranslation()

  const { user } = useAppSelector(userSelector)

  const [searchText, setSearchText] = useState('')
  const [players, setPlayers] = useState<Player[]>([])
  const [offset, setOffset] = useState(OFFSET)
  const [checked, setChecked] = useState<Player | null>(null)

  const { mutate: searchPlayer, data: resSearchPlayer, isLoading } = useSearchPlayer()

  const debouncedSearchText = useDebounce<string>(searchText || '*', 200)

  const onChangeMemberSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
    setPlayers([])
    setOffset(OFFSET)
  }

  const handleScroll = (event: any) => {
    const { target } = event
    const hasMore = resSearchPlayer?.result.hasMore

    if (target.scrollHeight - target.scrollTop === target.clientHeight && hasMore) {
      setOffset((pre) => pre + LIMIT)
    }
  }

  const onCloseModal = () => {
    onClose()
    setPlayers([])
    setSearchText('')
    setChecked(null)
    setOffset(OFFSET)
  }

  useEffect(() => {
    if (open)
      searchPlayer({
        limit: LIMIT,
        offset: offset,
        query: debouncedSearchText ? debouncedSearchText : '*',
        includeUnclaimedPlayers: false,
        ...(currentPlayer?.[0]?.id && {
          exclude: [currentPlayer?.[0]?.id],
        }),
        filter: {
          lat: user?.addresses?.[0]?.latitude || null,
          lng: user?.addresses?.[0]?.longitude || null,
          rating: {
            maxRating: null,
            minRating: null,
            type: currentBracketData.format,
          },
          ageRange: {
            maxAge: currentBracketData.ageBracket[1],
            minAge: currentBracketData.ageBracket[0],
          },
          gender:
            currentBracketData?.playerGroup === 'MEN' || currentBracketData?.playerGroup === 'MIXED'
              ? 'MALE'
              : currentBracketData?.playerGroup === 'WOMEN'
                ? 'FEMALE'
                : (null as any),
        },
      })
  }, [open, offset, debouncedSearchText, currentBracketData, user])

  useEffect(() => {
    if (resSearchPlayer) setPlayers([...players, ...resSearchPlayer.result.hits])
  }, [resSearchPlayer])

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onCloseModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col gap-y-4 max-w-5xl transform rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={onCloseModal}
                >
                  <Close className="text-navy-900" />
                </button>

                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('event.addAPartner')}
                </Dialog.Title>

                <div className="flex">
                  <p className="text-sm text-navy-900 text-opacity-70">{t('event.addAPartnerDesc')}</p>
                </div>

                <Search
                  searchText={searchText}
                  onChangeInput={onChangeMemberSearch}
                  className="py-3 px-4 w-full border gap-x-2"
                />

                <div className="flex flex-col gap-y-2 h-96 overflow-y-auto" onScroll={(e: any) => handleScroll(e)}>
                  {players?.map((item) => (
                    <BracketPlayerItem
                      key={item.id}
                      item={item}
                      className="border !rounded-lg"
                      showCheckBox
                      checked={checked}
                      setChecked={setChecked}
                    />
                  ))}
                  {isLoading && (
                    <>
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                    </>
                  )}
                </div>
                <div className="flex justify-center gap-x-6">
                  <button
                    onClick={onCloseModal}
                    className="bg-navy-900 hover:bg-opacity-20 bg-opacity-10 !py-[6px] !px-4 !rounded text-navy-900 font-semibold"
                  >
                    {t('common.cancel')}
                  </button>
                  <ButtonPrimary
                    title={t('event.addAPartner')}
                    className="!py-[6px] !px-4 !rounded"
                    disabled={!checked}
                    onClick={() => {
                      onSubmit(checked)
                      onCloseModal()
                    }}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        {viewQualifyingBrackets && closeViewBrackets && (
          <ViewQualifyingBracketsModal open={viewQualifyingBrackets} onClose={closeViewBrackets} />
        )}
      </Dialog>
    </Transition>
  )
}
